import { useState, useEffect, useRef } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Check, ChevronRight, Sparkles } from 'lucide-react';
import OrbisSpinner from '@/components/ui/orbis-spinner';

/* ── Deterministic task list for the loading experience ── */
const TASKS = [
  { label: 'Analyzing design tokens and brand constraints', duration: 4000 },
  { label: 'Mapping page architecture from project brief', duration: 5000 },
  { label: 'Generating sitemap and content hierarchy', duration: 6000 },
  { label: 'Defining section layouts for each page', duration: 9000 },
  { label: 'Applying typography system to page structures', duration: 10000 },
  { label: 'Configuring CTA strategy and conversion flow', duration: 10000 },
  { label: 'Writing content direction for each section', duration: 11000 },
  { label: 'Resolving design principles to layout rules', duration: 8000 },
  { label: 'Validating structure against UX guidelines', duration: 8000 },
  { label: 'Finalizing page interconnections and nav flow', duration: 9000 },
  { label: 'Preparing structure for review', duration: null },
];

/* ── Narrative messages — personalized via brief/designSystem ── */
function buildNarrativeMessages(brief, designSystem) {
  const company = brief?.company_name || 'your project';
  const styleName = designSystem?.style?.name || 'your chosen style';
  const headingFont = designSystem?.typography?.heading || 'your heading font';
  const vibe = brief?.vibe || 'your brand aesthetic';

  return [
    `Reading your design system — ${styleName} aesthetic, ${headingFont} typography, crafted surfaces with purposeful contrast. Got it.`,
    `Your brief says you need a website for ${company}. Mapping the primary conversion goal from your CTA strategy.`,
    `Designing the sitemap. Based on your CTA strategy, I'm prioritizing the hero above the fold with a direct path to action.`,
    `Generating sections for the page structure: confrontational opener, problem statement, solution reveal, social proof, urgent CTA.`,
    `Applying your typography system. ${headingFont} for headlines creates the high-contrast impact your style brief calls for.`,
    `Configuring your navigation: sticky horizontal bar, logo left, links center, single CTA button right. Clean and conversion-focused.`,
    `Writing section briefs for each page — content direction, not copy. These guide the AI builder in the Build step.`,
    `Checking layout against your UX guidelines — mobile first, smooth scroll, sticky nav with active state. All clear.`,
    `Almost there. Finalizing the connection between your design principles and the structure decisions. ${vibe} — that's locked into the layout.`,
    `Structure complete. Reviewing output quality before handing off...`,
  ];
}

/* ── Typewriter hook ── */
function useTypewriter(text, speed = 35, active = false) {
  const [displayed, setDisplayed] = useState('');
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!active || !text) { setDisplayed(''); setDone(false); return; }
    setDisplayed('');
    setDone(false);
    let i = 0;
    const interval = setInterval(() => {
      i++;
      setDisplayed(text.slice(0, i));
      if (i >= text.length) { clearInterval(interval); setDone(true); }
    }, speed);
    return () => clearInterval(interval);
  }, [text, speed, active]);

  return { displayed, done };
}

/* ── Task Item ── */
const TaskItem = ({ label, status, index, reducedMotion }) => (
  <motion.div
    initial={reducedMotion ? {} : { opacity: 0, x: -8 }}
    animate={{ opacity: 1, x: 0 }}
    transition={{ delay: index * 0.04, duration: 0.25 }}
    className="flex items-center gap-3 py-2 px-3 rounded-lg transition-colors"
    style={{
      background: status === 'active' ? 'var(--orbis-s1)' : 'transparent',
      border: status === 'active' ? '1px solid var(--orbis-border-md)' : '1px solid transparent',
    }}
  >
    <div className="w-5 h-5 flex items-center justify-center shrink-0">
      {status === 'pending' && (
        <div className="w-2 h-2 rounded-full" style={{ background: 'var(--orbis-s3)' }} />
      )}
      {status === 'active' && (
        <OrbisSpinner size={16} color="#B8CCDF" />
      )}
      {status === 'done' && (
        <motion.div
          initial={reducedMotion ? false : { scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', stiffness: 400, damping: 20 }}
          className="w-5 h-5 rounded-full flex items-center justify-center"
          style={{ background: 'rgba(52,211,153,0.12)' }}
        >
          <Check className="w-3 h-3" style={{ color: 'var(--orbis-positive)' }} />
        </motion.div>
      )}
    </div>
    <span style={{
      fontSize: '13px',
      fontFamily: 'var(--orbis-font)',
      color: status === 'active' ? 'var(--orbis-t1)' : status === 'done' ? 'var(--orbis-t3)' : 'var(--orbis-t4)',
      fontWeight: status === 'active' ? 500 : 400,
      transition: 'color 200ms',
    }}>
      {label}
    </span>
  </motion.div>
);

/* ── Narrative Message ── */
const NarrativeMessage = ({ text, isActive, isDone }) => {
  const { displayed, done } = useTypewriter(text, 35, isActive || isDone);

  return (
    <div className="flex gap-3 py-3 px-4 rounded-lg" style={{
      background: isActive ? 'var(--orbis-s1)' : 'transparent',
      border: isActive ? '1px solid var(--orbis-border-md)' : '1px solid transparent',
      transition: 'background 300ms, border-color 300ms',
    }}>
      <div className="w-6 h-6 rounded-md flex items-center justify-center shrink-0 mt-0.5" style={{
        background: isActive ? 'rgba(184,204,223,0.1)' : 'transparent',
      }}>
        <Sparkles className="w-3.5 h-3.5" style={{ color: isActive ? 'var(--orbis-blue)' : 'var(--orbis-t4)' }} />
      </div>
      <p style={{
        fontSize: '14px',
        lineHeight: 1.7,
        color: isActive ? 'var(--orbis-t1)' : 'var(--orbis-t3)',
        transition: 'color 300ms',
        minHeight: '1.7em',
      }}>
        {isDone ? text : displayed}
        {isActive && !done && <span className="animate-pulse" style={{ color: 'var(--orbis-blue)' }}>|</span>}
      </p>
    </div>
  );
};

/* ── Main StructureTransition Component ── */
export default function StructureTransition({ designSystem, brief, isComplete, onContinue }) {
  const reducedMotion = useReducedMotion();
  const [taskStatuses, setTaskStatuses] = useState(() => TASKS.map(() => 'pending'));
  const [activeMessageIdx, setActiveMessageIdx] = useState(-1);
  const [completedMessages, setCompletedMessages] = useState(new Set());
  const [elapsed, setElapsed] = useState(0);
  const [showContinue, setShowContinue] = useState(false);
  const startTime = useRef(Date.now());
  const feedRef = useRef(null);
  const narrativeMessages = useRef(buildNarrativeMessages(brief, designSystem)).current;
  const completedRef = useRef(false);

  // Elapsed time counter
  useEffect(() => {
    const interval = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startTime.current) / 1000));
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Browser tab title
  useEffect(() => {
    const prev = document.title;
    document.title = 'Building structure... · Orbis';
    return () => { document.title = prev; };
  }, []);

  // Deterministic task progression
  useEffect(() => {
    let cumulative = 0;
    const timers = [];

    TASKS.forEach((task, idx) => {
      if (idx === TASKS.length - 1) return;

      timers.push(setTimeout(() => {
        setTaskStatuses(prev => {
          const next = [...prev];
          next[idx] = 'active';
          return next;
        });
      }, cumulative));

      cumulative += task.duration;

      timers.push(setTimeout(() => {
        setTaskStatuses(prev => {
          const next = [...prev];
          next[idx] = 'done';
          return next;
        });
      }, cumulative));
    });

    timers.push(setTimeout(() => {
      setTaskStatuses(prev => {
        const next = [...prev];
        next[TASKS.length - 1] = 'active';
        return next;
      });
    }, cumulative));

    return () => timers.forEach(clearTimeout);
  }, []);

  // Narrative message progression
  useEffect(() => {
    const timers = [];
    let cumulative = 2000;

    narrativeMessages.forEach((_, idx) => {
      timers.push(setTimeout(() => {
        setActiveMessageIdx(idx);
      }, cumulative));

      const typingTime = narrativeMessages[idx].length * 35;
      cumulative += typingTime + 1800;

      timers.push(setTimeout(() => {
        setCompletedMessages(prev => new Set([...prev, idx]));
      }, cumulative - 1800));
    });

    return () => timers.forEach(clearTimeout);
  }, [narrativeMessages]);

  // When API completes — fast-forward remaining tasks
  useEffect(() => {
    if (!isComplete || completedRef.current) return;
    completedRef.current = true;

    document.title = 'Structure ready · Orbis';

    const fastForward = async () => {
      for (let i = 0; i < TASKS.length; i++) {
        setTaskStatuses(prev => {
          if (prev[i] !== 'done') {
            const next = [...prev];
            next[i] = 'done';
            return next;
          }
          return prev;
        });
        await new Promise(r => setTimeout(r, 300));
      }
      setTimeout(() => setShowContinue(true), 600);
    };
    fastForward();
  }, [isComplete]);

  // Auto-scroll feed
  useEffect(() => {
    if (feedRef.current) {
      feedRef.current.scrollTop = feedRef.current.scrollHeight;
    }
  }, [activeMessageIdx, completedMessages]);

  const progressPercent = isComplete ? 100 : Math.min(95, (elapsed / 80) * 95);
  const completedCount = taskStatuses.filter(s => s === 'done').length;
  const formatElapsed = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  return (
    <div className="flex flex-col w-full" style={{ height: 'calc(100vh - 64px)', background: 'var(--orbis-bg)' }}>
      <div className="flex flex-1 min-h-0">

      {/* ── Left Rail — Build Status ── */}
      <div className="w-[440px] shrink-0 flex flex-col" style={{ borderRight: '1px solid var(--orbis-border-md)' }}>
        {/* Header with progress */}
        <div style={{ padding: '16px 16px 12px' }}>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <OrbisSpinner size={20} color="#B8CCDF" />
              <span style={{ fontSize: '13px', fontWeight: 500, color: 'var(--orbis-t1)', fontFamily: 'var(--orbis-font)' }}>
                Building Structure
              </span>
            </div>
            <span style={{ fontSize: '11px', fontFamily: 'var(--orbis-mono)', color: 'var(--orbis-t3)' }}>
              {completedCount}/{TASKS.length}
            </span>
          </div>
          {/* Progress bar */}
          <div className="w-full h-[3px] rounded-full" style={{ background: 'var(--orbis-s2)' }}>
            <motion.div
              className="h-full rounded-full"
              style={{ background: isComplete ? 'var(--orbis-positive)' : 'var(--orbis-blue)' }}
              animate={{ width: `${progressPercent}%` }}
              transition={{ duration: isComplete ? 0.3 : 1, ease: 'linear' }}
            />
          </div>
        </div>

        {/* Task list */}
        <div className="flex-1 overflow-y-auto px-2" style={{ paddingBottom: 8 }}>
          {TASKS.map((task, idx) => (
            <TaskItem
              key={idx}
              label={task.label}
              status={taskStatuses[idx]}
              index={idx}
              reducedMotion={reducedMotion}
            />
          ))}
        </div>

        {/* Elapsed time */}
        <div className="px-4 py-3" style={{ borderTop: '1px solid var(--orbis-border-md)' }}>
          <div className="flex items-center justify-between">
            <span style={{ fontSize: '12px', fontFamily: 'var(--orbis-mono)', color: 'var(--orbis-t3)', letterSpacing: '0.05em' }}>
              {formatElapsed(elapsed)} elapsed
            </span>
            <span style={{ fontSize: '11px', color: 'var(--orbis-t4)' }}>
              Usually 60–90s
            </span>
          </div>
        </div>
      </div>

      {/* ── Center Canvas — AI Narrative Feed ── */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between" style={{ padding: '16px 20px', borderBottom: '1px solid var(--orbis-border-md)' }}>
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: 'var(--orbis-s1)', border: '1px solid var(--orbis-border-md)' }}>
              <Sparkles className="w-3.5 h-3.5" style={{ color: 'var(--orbis-blue)' }} />
            </div>
            <span style={{ fontSize: '15px', fontWeight: 500, color: 'var(--orbis-t1)', fontFamily: 'var(--orbis-font)' }}>
              AI Structure Agent
            </span>
          </div>
          <span style={{ fontSize: '13px', color: 'var(--orbis-t3)' }}>
            {brief?.company_name || ''}
          </span>
        </div>

        {/* Feed */}
        <div ref={feedRef} className="flex-1 overflow-y-auto" style={{ padding: '12px 16px' }}>
          <div className="space-y-2">
            {narrativeMessages.map((msg, idx) => {
              if (idx > activeMessageIdx && !completedMessages.has(idx)) return null;
              return (
                <NarrativeMessage
                  key={idx}
                  text={msg}
                  isActive={idx === activeMessageIdx && !completedMessages.has(idx)}
                  isDone={completedMessages.has(idx)}
                />
              );
            })}

            {/* Final completion message */}
            {showContinue && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex gap-3 py-3 px-4 rounded-lg mt-2"
                style={{ background: 'rgba(52,211,153,0.06)', border: '1px solid rgba(52,211,153,0.15)' }}
              >
                <div className="w-6 h-6 rounded-md flex items-center justify-center shrink-0 mt-0.5" style={{ background: 'rgba(52,211,153,0.12)' }}>
                  <Check className="w-3.5 h-3.5" style={{ color: 'var(--orbis-positive)' }} />
                </div>
                <p style={{ fontSize: '14px', lineHeight: 1.7, color: 'var(--orbis-t1)' }}>
                  Done. Your website structure is ready to review.
                </p>
              </motion.div>
            )}

            {/* Thinking indicator */}
            {!isComplete && activeMessageIdx >= narrativeMessages.length - 1 && completedMessages.has(narrativeMessages.length - 1) && (
              <div className="flex items-center gap-3 py-4 px-4">
                <OrbisSpinner size={20} color="#B8CCDF" />
                <span style={{ fontSize: '13px', color: 'var(--orbis-t3)' }}>
                  Processing the final details...
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Right Panel — Frozen Design System Summary ── */}
      <div className="w-[280px] shrink-0 overflow-y-auto" style={{ borderLeft: '1px solid var(--orbis-border-md)' }}>
        {/* Approved banner */}
        <div className="flex items-center gap-2" style={{ padding: '12px 16px', background: 'rgba(52,211,153,0.04)', borderBottom: '1px solid var(--orbis-border-md)' }}>
          <div className="w-5 h-5 rounded-full flex items-center justify-center" style={{ background: 'rgba(52,211,153,0.12)' }}>
            <Check className="w-3 h-3" style={{ color: 'var(--orbis-positive)' }} />
          </div>
          <span style={{ fontSize: '11px', color: 'var(--orbis-positive)', fontFamily: 'var(--orbis-mono)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
            Design System Approved
          </span>
        </div>

        <div className="space-y-5" style={{ padding: '16px' }}>
          {/* Color palette */}
          <div>
            <div style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--orbis-t4)', fontFamily: 'var(--orbis-mono)', marginBottom: '8px' }}>
              Color Palette
            </div>
            <div className="flex gap-1.5">
              {designSystem?.colors && Object.entries(designSystem.colors).map(([key, val]) => (
                <div key={key} className="text-center">
                  <div className="w-9 h-9 rounded-md" style={{ background: val, border: '1px solid var(--orbis-border-md)' }} />
                  <div style={{ fontSize: '9px', color: 'var(--orbis-t4)', fontFamily: 'var(--orbis-mono)', marginTop: '3px' }}>
                    {key}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Typography */}
          <div>
            <div style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--orbis-t4)', fontFamily: 'var(--orbis-mono)', marginBottom: '8px' }}>
              Typography
            </div>
            <div className="space-y-1">
              <div style={{ fontSize: '18px', fontWeight: 600, color: 'var(--orbis-t1)' }}>
                {designSystem?.typography?.heading || 'Heading Font'}
              </div>
              <div style={{ fontSize: '14px', color: 'var(--orbis-t3)' }}>
                {designSystem?.typography?.body || 'Body Font'}
              </div>
            </div>
          </div>

          {/* Style */}
          <div>
            <div style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--orbis-t4)', fontFamily: 'var(--orbis-mono)', marginBottom: '8px' }}>
              Style
            </div>
            <div style={{ fontSize: '14px', fontWeight: 500, color: 'var(--orbis-t1)' }}>
              {designSystem?.style?.name || 'Design Style'}
            </div>
          </div>

          {/* Visual personality */}
          {designSystem?.visual_spec && (
            <div>
              <div style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--orbis-t4)', fontFamily: 'var(--orbis-mono)', marginBottom: '8px' }}>
                Visual Personality
              </div>
              <div className="space-y-0">
                {Object.entries(designSystem.visual_spec).slice(0, 6).map(([key, val]) => (
                  <div key={key} className="flex justify-between py-1.5" style={{ borderBottom: '1px solid var(--orbis-s1)' }}>
                    <span style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--orbis-t4)', fontFamily: 'var(--orbis-mono)' }}>
                      {key.replace(/_/g, ' ')}
                    </span>
                    <span style={{ fontSize: '12px', color: 'var(--orbis-t3)', maxWidth: '140px', textAlign: 'right', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {typeof val === 'string' ? val : JSON.stringify(val)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      </div>{/* end horizontal flex row */}

      {/* ── Bottom Bar ── */}
      <div className="magic-bottom-bar">
        <div style={{ width: '180px' }} />

        <span style={{ fontSize: '12px', color: 'var(--orbis-t4)', fontFamily: 'var(--orbis-mono)' }}>
          {showContinue ? (
            <span className="flex items-center gap-2">
              <Check className="w-3.5 h-3.5" style={{ color: 'var(--orbis-positive)' }} />
              Structure ready
            </span>
          ) : (
            <span className="flex items-center gap-2">
              <OrbisSpinner size={14} color="#B8CCDF" />
              Generating structure
            </span>
          )} · Step 3 of 8
        </span>

        <div className="orbis-cta-ring rounded-full p-0.5">
          <button
            onClick={showContinue ? onContinue : undefined}
            disabled={!showContinue}
            className="orbis-cta flex items-center justify-center gap-2 transition-all rounded-full"
            style={{
              width: '220px', height: '44px',
              opacity: showContinue ? 1 : 0.3,
              fontSize: '13px', fontWeight: 500,
              cursor: showContinue ? 'pointer' : 'not-allowed',
              fontFamily: 'var(--orbis-font)',
            }}
          >
            {showContinue ? (
              <>Continue <ChevronRight className="w-4 h-4" /></>
            ) : (
              <>Generating...</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
